import { useEffect, useState } from 'react';
import { SearchBackend, SearchType } from '@ardoq/api-types';
import { connect, spreadMap } from '@ardoq/rxbeach';
import { GremlinSearchStateShape } from 'search/Gremlin/types';
import { usePaginationGremlinSearch } from 'search/Gremlin/usePaginationGremlinSearch';
import { GremlinSearchError } from 'search/Gremlin/GremlinResults/GremlinSearchError';
import { GremlinResults } from 'search/Gremlin/GremlinResults/GremlinResults';
import { metamodelQueryResults$ } from 'search/Gremlin/gremlinResults$';
import { LoadingWrapper } from '@ardoq/status-ui';
import { ArdoqLoaderSpinner, LoaderColor, LoaderSize } from '@ardoq/loader';
import { FlexBox } from '@ardoq/layout';
import { combineLatest } from 'rxjs';
import { fields$ } from 'streams/fields/fields$';
import type { FieldLabelByName } from 'models/utils/fieldOps';
import { keyBy, mapValues } from 'lodash';
import { i18nLocale$ } from 'streams/i18nLocale$';
import type { I18nLocale } from '@ardoq/i18n';
import { locale$ } from 'streams/locale$';
import type { Locale } from '@ardoq/locale';

type OwnProps = {
  query: string;
};

const GremlinSearchTableWithLoaderBase = ({
  query,
  results,
  totalResults,
  showRawResults,
  syntaxError,
  searchError,
  fieldLabelByName,
  locale,
  i18nLocale,
}: OwnProps &
  GremlinSearchStateShape & {
    fieldLabelByName: FieldLabelByName;
    locale: Locale;
    i18nLocale: I18nLocale;
  }) => {
  const model = {
    backend: SearchBackend.GREMLIN,
    name: '',
    parameterQueryId: null,
    query,
    supportedParams: [],
    type: SearchType.METAMODEL_QUERY,
  };

  const { resultsId, loadPaginatedResults, doPaginatedSearch } =
    usePaginationGremlinSearch({
      model,
      queryParams: {},
      searchType: SearchType.METAMODEL_QUERY,
    });

  const [isSearching, setIsSearching] = useState(true);
  useEffect(() => {
    setIsSearching(true);
    doPaginatedSearch();
    // Adding doPaginatedSearch as a dependency here will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    setIsSearching(false);
  }, [results]);

  return (
    <>
      {searchError && (
        <GremlinSearchError syntaxError={syntaxError} i18nLocale={i18nLocale} />
      )}
      {results && (
        <LoadingWrapper loading={isSearching}>
          <GremlinResults
            result={results}
            showRaw={showRawResults}
            loadResults={loadPaginatedResults}
            totalResults={totalResults}
            resultsId={resultsId}
            fieldLabelByName={fieldLabelByName}
            locale={locale}
            i18nLocale={i18nLocale}
          />
        </LoadingWrapper>
      )}
      {!searchError && !results && (
        <FlexBox justify="center" paddingTop="xxlarge">
          <ArdoqLoaderSpinner
            color={LoaderColor.GRAY}
            size={LoaderSize.MEDIUM}
          />
        </FlexBox>
      )}
    </>
  );
};

export const GremlinSearchTableWithLoader = connect(
  GremlinSearchTableWithLoaderBase,
  combineLatest([metamodelQueryResults$, fields$, locale$, i18nLocale$]).pipe(
    spreadMap((gremlinResults, fieldsCollection, locale, i18nLocale) => ({
      ...gremlinResults,
      fieldLabelByName: mapValues(
        keyBy(fieldsCollection.models, 'name'),
        'label'
      ),
      locale,
      i18nLocale,
    }))
  )
);
